import {
    TicketMessageEntity,
} from "@dropDesk/domain/entities/ticket/message/ticket_message.entity";
import React, {useMemo} from "react";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import MessageBox from "@dropDesk/presentation/components/messages/message_box";
import firebaseStorage from "firebase/storage";
import {
    ReplyEntity
} from "@dropDesk/domain/entities/ticket/message/external/message_entity";
import TicketHeader from "@dropDesk/presentation/components/chat_view/chat_message/message_box/ticket_header";
import UploadTask = firebaseStorage.UploadTask;
import {
    useCanChangeReaction, useDeleteMessageButton, useEditMessageButton,
    useGetStatus,
    useImagesMessage, useReactionButton, useReplyButton
} from "@dropDesk/presentation/components/chat_view/chat_message/message_box/use_message_action";
import {observer} from "mobx-react";
import {ChatMessageEntity} from "@dropDesk/domain/entities/messages/message_enums";
import {InternalChatMessageEntity} from "@dropDesk/domain/entities/internal_chat/messages/internal_chat_message_entity";

const MessagesBox =
    observer(({
                  messages,
                  userClient,
                  attendant,
                  idMessageFocused,
                  handleClickOnMessageReply,
                  cancelUpload,
                  onRetrySendMessageError,
                  canChange,
                  userLogged,
                  handleRemoveReaction,
                  handleAddReaction,
                  lastUsedReactions,
                  setMessageToReply,
                  handleDeleteMessage,
                  handleEditMessage,
                  handleResetMessageFocused,
                  compactModeMessages,
                  idParent,
                  allReactions,
                  isMeAttendingTicket,
                  ticketIsOpen,
                  showOriginalFile,
                  setLoaded,
                  startingServiceInternalChatHeaderText,
                  closingServiceInternalChatHeaderText,
              }: {
                  messages: ChatMessageEntity[],
                  userClient: UserEntity | null,
                  attendant?: UserEntity,
                  idMessageFocused: string | null
                  handleClickOnMessageReply: (reply: ReplyEntity) => void
                  cancelUpload: (uploadTask?: UploadTask) => void
                  onRetrySendMessageError: (message: ChatMessageEntity) => void
                  canChange: boolean
                  userLogged: UserEntity
                  handleRemoveReaction: (reaction: ChatMessageEntity, messageReacted: ChatMessageEntity) => Promise<void>
                  handleAddReaction: (reaction: string, messageToReacted: ChatMessageEntity) => Promise<void>
                  lastUsedReactions: string[]
                  setMessageToReply: (message: ReplyEntity | null) => void
                  handleDeleteMessage: (message: ChatMessageEntity) => void
                  handleEditMessage: (editText: string, currentMessage: ChatMessageEntity) => void
                  handleResetMessageFocused: () => void
                  compactModeMessages: boolean,
                  idParent: string,
                  allReactions: ChatMessageEntity[]
                  isMeAttendingTicket: boolean
                  ticketIsOpen: boolean
                  showOriginalFile: (message: ChatMessageEntity) => void
                  setLoaded: (message: ChatMessageEntity) => void
                  startingServiceInternalChatHeaderText?: string
                  closingServiceInternalChatHeaderText?: string
              }
    ) => {

        const messageBoxProps = useMemo(() => {
            return messages.map((message) => ({
                message,
                useReplyButton: useReplyButton(message, canChange, idParent, isMeAttendingTicket, ticketIsOpen, userLogged),
                useDeleteMessageButton: useDeleteMessageButton(message, canChange, userLogged, idParent),
                status: useGetStatus(message, userClient, userLogged, idParent, attendant),
                useEditMessageButton: useEditMessageButton(message, canChange, userLogged, idParent),
                canRemoveReaction: useCanChangeReaction(message, canChange, idParent),
                reactionButton: useReactionButton(message, canChange, idParent, isMeAttendingTicket, userLogged),
                imageMessages: useImagesMessage(messages),
                reactions: allReactions.filter(entry => (entry.reactionMessage!.messageReactedId === message.idExternal || entry.reactionMessage!.messageReactedId === message.id) && !entry.deleted) ?? []
            }));
        }, [messages, userClient, userLogged, canChange, idParent, isMeAttendingTicket, ticketIsOpen, allReactions, attendant]);
        return (
            <>
                {messageBoxProps.map(({
                                          message,
                                          useReplyButton,
                                          useDeleteMessageButton,
                                          status,
                                          useEditMessageButton,
                                          canRemoveReaction,
                                          reactionButton,
                                          imageMessages,
                                          reactions
                                      }) => (
                    <div key={message.id} id={message.id}>

                        {(message instanceof TicketMessageEntity && (message.isFirstPosition || message.isUniquePosition) && message.ticket) &&
                            <TicketHeader text={message.ticket.startingServiceTicketHeaderText} isInit={true}/>}
                        {(message instanceof InternalChatMessageEntity && (message.isFirstPosition || message.isUniquePosition) && startingServiceInternalChatHeaderText) &&
                            <TicketHeader text={startingServiceInternalChatHeaderText} isInit={true}/>}

                        <MessageBox
                            message={message}
                            useReplyButton={useReplyButton}
                            onReplyClick={() => setMessageToReply(message.toReplyEntity)}
                            useDeleteMessageButton={useDeleteMessageButton}
                            resetMessageFocused={() => setTimeout(() => handleResetMessageFocused(), 1500)}
                            focus={!!idMessageFocused && (idMessageFocused === message.idExternal || idMessageFocused === message.id)}
                            onReplyMessageClick={(messageReply) => handleClickOnMessageReply(messageReply)}
                            onRetrySendMessageError={(message) => onRetrySendMessageError(message)}
                            status={status}
                            cancelUpload={(uploadTask) => cancelUpload(uploadTask)}
                            useEditMessageButton={useEditMessageButton}
                            canRemoveReaction={canRemoveReaction}
                            handleRemoveReaction={handleRemoveReaction}
                            handleAddReaction={handleAddReaction}
                            lastUsedReactions={lastUsedReactions}
                            userClient={userClient}
                            handleDeleteMessage={handleDeleteMessage}
                            handleEditMessage={handleEditMessage}
                            compactMode={compactModeMessages}
                            reactions={reactions}
                            useReactionButton={reactionButton}
                            attendant={attendant}
                            imageMessages={message.isValidPreviewMessage ? imageMessages : []}
                            showOriginalFile={showOriginalFile}
                            setLoaded={setLoaded}
                        />
                        {(message instanceof TicketMessageEntity && (message.isLastPosition || message.isUniquePosition) && message.ticket) &&
                            <TicketHeader text={message.ticket.closingServiceTicketHeaderText} isInit={false}/>}
                        {(message instanceof InternalChatMessageEntity && (message.isLastPosition || message.isUniquePosition) && closingServiceInternalChatHeaderText) &&
                            <TicketHeader text={closingServiceInternalChatHeaderText} isInit={false}/>}
                    </div>
                ))}

            </>
        )
    });
export default MessagesBox;
